
import React, { useContext, useEffect, useState } from "react";
import { Form, OverlayTrigger, Popover } from "react-bootstrap";

import { useTranslation, withTranslation } from "react-i18next";
import { BsInfoCircle } from "react-icons/bs";
import { LoaderContext, RegulationContext } from "../../../App";
import useBasicGraphCard from "../../../core/hooks/useBasicGraphCard";
import DashGraphConfigService from "../../../core/service/DashGraphConfigService";
import Utils from "../../../core/utilities/Utils";
import "./../CompanyDashboard/CompanyDashboard.scss";

import html2canvas from 'html2canvas';
import { AiOutlineDrag } from "react-icons/ai";
import { BiTrendingDown, BiTrendingUp } from "react-icons/bi";
import { CgClose } from "react-icons/cg";
import { toast } from 'react-toastify';
import ReportService from "../../../core/service/ReportService";

const CompanyGraphCard = ({ t, config, year, renderedOn, isAutoGenerateGraphImg, exportRef, onExport, graphType = 'ESG' }) => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const { glbRegulation } = useContext(RegulationContext);
  const standardId = glbRegulation?.id ? glbRegulation.id : "";
  const [graph, setGraph] = useState(null);
  const [activeInterval, setActiveInterval] = useState('MONTHLY');
  const {
    renderMoreOptions,
    getChart,
    renderDisplayModal,
    onDeactivate,
    chartData, setChartData,
    setChartOptions,
    chartRef
  } = useBasicGraphCard(t);
  const [showDatalabel, setShowDatalabel] = useState(isAutoGenerateGraphImg ? true : false);
  const [theme, setTheme] = useState(localStorage.getItem('theme'))
  const { k, i18n } = useTranslation();
  const { setLoaderDisplay } = useContext(LoaderContext);

  useEffect(() => {
    if (graphType === 'GHG_EMISSION') {
      setupGraphForGHGEmission();
    } else {
      getGraph(config?.id, year, standardId);
    }
  }, [config?.id, year, renderedOn, standardId]);

  useEffect(() => {
    setTheme(localStorage.getItem('theme'));
    setupDataForTheGraph(graph);
  }, [showDatalabel]);

  useEffect(() => {
    setupDataForTheGraph(graph);
  }, [i18n.language]);

  
  const getGraph = (id, y, stdId, interval = '', yearOffset = null) => {
    setLoaderDisplay(true);
    DashGraphConfigService.getGraph(id, `${y}-01-01`, stdId, null, interval, yearOffset)
      .then(res => {
        setLoaderDisplay(false);
        if (res.status !== 200) return;
        
        setupGraphFrom(res.data.data, id, interval, yearOffset);
        if (isAutoGenerateGraphImg) {
          setTimeout(() => {
            exportGraphImages();
          }, 1000);
        }
      }).catch(err => { });
  };

  const exportGraphImages = async () => {
    if (!exportRef.current) return;

    setLoaderDisplay(true);
    const canvas = await html2canvas(exportRef.current, { scale: 1, useCORS: true });
    const imageBlob = await new Promise(resolve => canvas.toBlob(resolve, "image/png", 0.7));
    if (!imageBlob) {
      toast.warning("Failed to add the graph to the editor", {
        position: toast.POSITION.TOP_RIGHT,
      });
      setLoaderDisplay(false);
      return;
    }
    const formData = new FormData();
    formData.append("file", imageBlob, "graph_data.png");
    ReportService.uploadImage(formData)
      .then((response) => {
        const imgRes = response.data;
        const graphImageURL = ReportService.getImage(imgRes.ref);

        onExport(graphImageURL);
      })
      .catch((error) => {
        setLoaderDisplay(false);
      })
      .finally(() => {
        setLoaderDisplay(false);
      });
  };

  const transformObject = (obj) => {
    const transformedObj = { ...obj };
    // Add the translated values to the main object based on languageCode
    if (obj.localizedContents && obj.localizedContents.length > 0) {
        obj.localizedContents.forEach((item) => {
            if (item.fieldName === 'title') {
                transformedObj[item.languageCode] = item.translatedValue;
            } else if (item.fieldName === 'description') {
              transformedObj[`${item.languageCode}_description`] = item.translatedValue;
          }
        });
    }
    // Recursively process the children objects
    // if (obj.metricData && obj.metricData.length > 0) {
    //     transformedObj.metricData = obj.metricData.map((child) => transformObject(child));
    // }

    return transformedObj;
};

  const setupDataForTheGraph = (g) => {
    if (g?.type === 'LINE') {
      setupLineChartData(g);
    } else if (g?.type === 'PIE') {
      setupPieChartData(g);
    } else if (g?.type === 'BAR' || g?.type === 'STACKED BAR' || g?.type === 'HORIZONTAL BAR' || g?.type === 'HORIZONTAL STACKED BAR') {
      setupBarChartData(g);
    } else if (g?.type === 'KPI') {
      setupKPIChartData(g);
    }
  };

    // Function to get localized label for a given field
    const getLocalizedLabel = (localizedContents, languageCode, fieldName) => {
      const localized = localizedContents?.find(
        (content) => content.languageCode == languageCode && content.fieldName == fieldName
      );
      return localized ? localized.translatedValue : null;
    };

  const setupLineChartData = (g) => {
    const dpOptions = g?.dataPointOptions?.filter(o => !o.position);
    let dataPoints = g?.dataPoints?.sort((a, b) => a.order - b.order);
    if (isYearlyInterval(g)) {
      dataPoints = g?.dataPoints?.sort((a, b) => +(a.xValue) - +(b.xValue));
    }
    const datasets = [];
    dpOptions?.forEach(option => {
      const color = Utils.getDecodedHexForGraphColor(option.color);
      // Populate data
      const data = [];
      let translatedLabel = '';
      for (let i = 0; i < dataPoints?.length; i++) {
        const dp = dataPoints[i];
        
        const value = dp.yValues?.find(y => y.label === option.label)?.value ?? 0;
        const dpCurrentVal = dp.yValues?.find(y => y.label === option.label);
        if (isMonthyInterval(g)) {
          if (year == currentYear) {
            if (i <= currentMonth) {
              data.push(value);
            }
          } else {
            data.push(value);
          }
        } else {
          data.push(value);
        }
        translatedLabel = getLocalizedLabel(dpCurrentVal?.localizedContents, i18n.language, 'label') ?? option.label;
      };

      datasets.push({
        label: translatedLabel,
        // label:translatedLabel,
        borderColor: color.borderColor,
        backgroundColor: config?.fillAsGradient ? getGradientBackgroundColor(color) : color.backgroundColor,
        data: data,
        dp: option,
        tension: 0.4,
        fill: config?.fill,
        pointRadius: config?.hidePoints,
      });
    });
    const labels = [];
    // dataPoints?.forEach(dp => {
    //   if (isMonthyInterval(g)) {
    //     labels.push(dp.xValue?.substring(0, 3));
    //   } else {
    //     labels.push(dp.xValue);
    //   }
    // });
    dataPoints?.forEach(dp => {
      if (isMonthyInterval(g)) {
        // Fix: Add day and year to make it a valid date
        const date = new Date(`${dp.xValue} 1, 2024`);
        if (!isNaN(date.getTime())) {
          const monthName = new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(date);
          labels.push(monthName);
        } else {
          console.warn("Still invalid date after patching:", dp.xValue);
          labels.push(dp.xValue); // fallback
        }
      } else {
        labels.push(dp.xValue);
      }
    });
    

    const cOption = {
      layout: {
        padding: {
          top: 20,
          bottom: 5,
          left: 5,
          right: 5,
        },
      },
      responsive: true,
      plugins: {
        legend: {
          position: g?.overrideLegendPosition ? getLegendPosition(g) : "bottom",
          align: g?.overrideLegendPosition ? getLegendAlign(g) : 'start',
          labels: {
            font: {
              size: isAutoGenerateGraphImg ? 25 : 10
            }
          },
          display: config?.hideLegend ? false : true
        },
        datalabels: {
          display: true,
          anchor: 'start',
          align: 'top',
          clip: false,
          color: theme == 'dark' ? '#fff' : 'light' ? '#000' : '',
          font: {
            size: isAutoGenerateGraphImg ? 20 : 10
          },
          formatter: function (value, context) {
            if (showDatalabel) {
              return value != 0 ? Utils.formattedNumber(value) : null;
            } else {
              return null
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (tooltipItem) => {
              return ` ${Utils.formattedNumber(tooltipItem?.dataset?.data[tooltipItem.dataIndex])} ${tooltipItem?.dataset?.dp?.unitOfMeasure ?? ''}`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: {
            clip: false,
            font: {
              size: isAutoGenerateGraphImg ? 20 : 10
            },
          },
          grid: {
            display: config?.gridLines,
            color: 'rgba(200, 200, 200, 0.2)' // Set grid line color for X-axis
          }
        },
        y: {
          ticks: {
            clip: false,
            font: {
              size: isAutoGenerateGraphImg ? 20 : 10
            },
          },
          grid: {
            display: config?.gridLines,
            color: 'rgba(200, 200, 200, 0.2)' // Set grid line color for X-axis
          },
          suggestedMax: findMaxYValue(dataPoints) + 100
        },
      },
    };
    setChartOptions(cOption);
    setChartData({
      labels,
      datasets: datasets,
    });
  };

  const setupPieChartData = (g) => {

    const dpOptions = g?.dataPointOptions?.filter(o => !o.position);
    const dataPoints = g?.dataPoints?.sort((a, b) => a.order - b.order);
    const data = [], labels = [], bgColors = [], borderColors = [], dpUOMs = [];
    const colorData = {};
    dpOptions?.forEach(option => {
      colorData[option.label] = Utils.getDecodedHexForGraphColor(option.color);
      dpUOMs.push(option.unitOfMeasure);
    });
    dataPoints[0]?.yValues?.forEach(yValue => {
      // labels.push(yValue.label);
      labels.push(getLocalizedLabel(yValue?.localizedContents, i18n.language, 'label') ?? yValue.label);
      data.push(yValue.value);
      bgColors.push(colorData[yValue.label].backgroundColor);
      borderColors.push(colorData[yValue.label].borderColor);
    });
    const cOption = {
      layout: {
        padding: {
          top: 20,
          bottom: 5,
          left: 5,
          right: 5,
        },
      },
      responsive: true,
      plugins: {
        legend: {
          position: getLegendPositionForPieChart(g),
          display: true,
          labels: {
            font: {
              size: isAutoGenerateGraphImg ? 25 : 10
            }
          }
        },
        title: {
          display: false,
          text: "",
        },
        datalabels: {
          display: true,
          anchor: (ctx) => {
            return ctx.dataIndex % 2 === 0 ? 'center' : 'start';
          },
          align: (ctx) => {
            return ctx.dataIndex % 2 === 0 ? 'start' : 'center';
          },
          offset: 0,
          clip: false,
          color: theme == 'dark' ? '#fff' : 'light' ? '#000' : '',
          font: {
            size: isAutoGenerateGraphImg ? 20 : 10
          },
          formatter: function (value, context) {
            if (showDatalabel) {
              return value != 0 ? Utils.formattedNumber(value) : null;
            } else {
              return null
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (tooltipItem) => {
              return ` ${Utils.formattedNumber(tooltipItem?.dataset?.data[tooltipItem.dataIndex])} ${tooltipItem?.dataset?.uoms[tooltipItem.dataIndex] ?? ''}`;
            }
          }
        }
      },
      maintainAspectRatio: false,
    };

    setChartOptions(cOption);
    setChartData({
      options: cOption,
      labels: labels,
      datasets: [{
        label: '',
        data: data,
        backgroundColor: bgColors,
        borderColor: borderColors,
        // borderWidth: 1.5,
        uoms: dpUOMs
      }],
    });
  };

  const setupBarChartData = (g) => {
    const dpOptions = g?.dataPointOptions?.filter(o => !o.position);
    let dataPoints = g?.dataPoints?.sort((a, b) => a.order - b.order);
    if (isYearlyInterval(g)) {
      dataPoints = g?.dataPoints?.sort((a, b) => +(a.xValue) - +(b.xValue));
    }
    const datasets = [];
    dpOptions?.forEach(option => {
      const color = Utils.getDecodedHexForGraphColor(option.color);
      // Populate data
      const data = [];
      let translatedLabel = '';
      for (let i = 0; i < dataPoints?.length; i++) {
        const dp = dataPoints[i];
        const value = dp.yValues?.find(y => y.label === option.label)?.value ?? 0;
        const dpCurrentVal = dp.yValues?.find(y => y.label === option.label);
        translatedLabel = getLocalizedLabel(dpCurrentVal?.localizedContents, i18n.language, 'label') ?? option.label;
        if (isMonthyInterval(g)) {
          if (year == currentYear) {
            if (i <= currentMonth) {
              data.push(value);
            }
          } else {
            data.push(value);
          }
        } else {
          data.push(value);
        }
      }
      datasets.push({
        // label: option.label,
        label: translatedLabel,
        borderColor: color.borderColor,
        backgroundColor: color.backgroundColor,
        data: data,
        // borderWidth: 1.5,
        dp: option
      });
    });
    const labels = [];
    dataPoints?.forEach(dp => {
      if (isMonthyInterval(g)) {
        // Fix: Add day and year to make it a valid date
        const date = new Date(`${dp.xValue} 1, 2024`);
        if (!isNaN(date.getTime())) {
          const monthName = new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(date);
          labels.push(monthName);
        } else {
          console.warn("Still invalid date after patching:", dp.xValue);
          labels.push(dp.xValue); // fallback
        }
      } else {
        labels.push(dp.xValue);
      }
    });    
    setChartData({
      labels: labels,
      datasets: datasets
    });
    if (g?.type === 'STACKED BAR' || g?.type === 'HORIZONTAL STACKED BAR') {
      setChartOptions({
        responsive: true,
        indexAxis: g?.type === 'STACKED BAR' ? 'x' : 'y',
        layout: {
          padding: {
            top: 20,
            bottom: 5,
            left: 5,
            right: 5,
          },
        },
        plugins: {
          legend: {            
            position: g?.overrideLegendPosition ? getLegendPosition(g) : "bottom",
            align: g?.overrideLegendPosition ? getLegendAlign(g) : 'start',
            labels: {
              font: {
                size: isAutoGenerateGraphImg ? 25 : 10
              }
            }
          },
          title: {
            display: false,
          },
          datalabels: {
            display: (context) => context.datasetIndex === context.chart.data.datasets.length - 1,
            anchor: 'end',
            align: 'top',
            clip: false,
            color: theme == 'dark' ? '#fff' : 'light' ? '#000' : '',
            formatter: (_, context) => {
              const stackTotal = context.chart.data.datasets
                .reduce((acc, dataset) => acc + dataset.data[context.dataIndex], 0);
              if (showDatalabel) {
                return (stackTotal != 0 ? Utils.formattedNumber(stackTotal) : null);
              } else {
                return null
              }
            },
            font: {
              size: isAutoGenerateGraphImg ? 20 : 10
            }
          },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => {
                return ` ${Utils.formattedNumber(tooltipItem?.dataset?.data[tooltipItem.dataIndex])} ${tooltipItem?.dataset?.dp?.unitOfMeasure ?? ''}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: {
              clip: false,
              font: {
                size: isAutoGenerateGraphImg ? 20 : 10
              },
            },
            stacked: true,
            grid: {
              display: config?.gridLines,
              color: 'rgba(200, 200, 200, 0.2)' // Set grid line color for X-axis
            }
          },
          y: {
            ticks: {
              clip: false,
              font: {
                size: isAutoGenerateGraphImg ? 20 : 10
              },
            },
            stacked: true,
            grid: {
              display: config?.gridLines,
              color: 'rgba(200, 200, 200, 0.2)' // Set grid line color for X-axis
            },
            suggestedMax: findMaxYValue(dataPoints) + 100
          },
        },
      });
    } else if (g?.type === 'HORIZONTAL BAR') {
      setChartOptions({
        responsive: true,
        indexAxis: g?.type === 'BAR' ? 'x' : 'y',
        plugins: {
          legend: {
            position: g?.overrideLegendPosition ? getLegendPosition(g) : "bottom",
            align: g?.overrideLegendPosition ? getLegendAlign(g) : 'start',
            labels: {
              font: {
                size: isAutoGenerateGraphImg ? 25 : 10
              }
            }
          },
          datalabels: {
            display: true,
            anchor: 'end',
            align: 'right',
            clip: false,
            color: theme == 'dark' ? '#fff' : 'light' ? '#000' : '',
            font: {
              size: isAutoGenerateGraphImg ? 20 : 10
            },
            formatter: function (value, context) {
              if (showDatalabel) {
                return value != 0 ? Utils.formattedNumber(value) : null;
              } else {
                return null
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => {
                return ` ${Utils.formattedNumber(tooltipItem?.dataset?.data[tooltipItem.dataIndex])} ${tooltipItem?.dataset?.dp?.unitOfMeasure ?? ''}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: {
              clip: false,
              font: {
                size: isAutoGenerateGraphImg ? 20 : 10
              },
            },
            grid: {
              display: config?.gridLines,
              color: 'rgba(200, 200, 200, 0.2)' // Set grid line color for X-axis
            },
            suggestedMax: g?.type === 'HORIZONTAL BAR' ? findMaxYValue(dataPoints) + 100 : undefined
          },
          y: {
            ticks: {
              clip: false,
              font: {
                size: isAutoGenerateGraphImg ? 20 : 10
              },
            },
            grid: {
              display: config?.gridLines,
              color: 'rgba(200, 200, 200, 0.2)' // Set grid line color for X-axis
            },
            suggestedMax: g?.type === 'BAR' ? findMaxYValue(dataPoints) + 100 : undefined
          },
        },
      });
    } else {
      setChartOptions({
        layout: {
          padding: {
            top: 20,
            bottom: 5,
            left: 5,
            right: 5,
          },
        },
        responsive: true,
        indexAxis: g?.type === 'BAR' ? 'x' : 'y',
        plugins: {
          legend: {           
            position: g?.overrideLegendPosition ? getLegendPosition(g) : "bottom",
            align: g?.overrideLegendPosition ? getLegendAlign(g) : 'start',
            labels: {
              font: {
                size: isAutoGenerateGraphImg ? 25 : 10
              }
            }
          },
          datalabels: {
            display: true,
            anchor: 'end',
            align: 'top',
            clip: false,
            color: theme == 'dark' ? '#fff' : 'light' ? '#000' : '',
            font: {
              size: isAutoGenerateGraphImg ? 20 : 10
            },
            formatter: function (value, context) {
              if (showDatalabel) {
                return value != 0 ? Utils.formattedNumber(value) : null;
              } else {
                return null
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => {
                return ` ${Utils.formattedNumber(tooltipItem?.dataset?.data[tooltipItem.dataIndex])} ${tooltipItem?.dataset?.dp?.unitOfMeasure ?? ''}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: {
              clip: false,
              font: {
                size: isAutoGenerateGraphImg ? 20 : 10
              },
            },
            grid: {
              display: config?.gridLines,
              color: 'rgba(200, 200, 200, 0.2)' // Set grid line color for X-axis
            }
          },
          y: {
            ticks: {
              clip: false,
              font: {
                size: isAutoGenerateGraphImg ? 20 : 10
              },
            },
            grid: {
              display: config?.gridLines,
              color: 'rgba(200, 200, 200, 0.2)' // Set grid line color for X-axis
            },
            suggestedMax: findMaxYValue(dataPoints) + 100
          },
        },
      });
    }
  };

  const setupKPIChartData = (g) => {
    const dpOptions = g?.dataPointOptions?.filter(o => !o.position);
    const dataPoints = g?.dataPoints?.sort((a, b) => a.order - b.order);
    const datasets = [];
    dpOptions?.forEach(option => {
      let translatedLabel = '';
      const data = [];
      for (let i = 0; i < dataPoints?.length; i++) {
        const dp = dataPoints[i];
        const value = dp.yValues?.find(y => y.label === option.label)?.value ?? 0;
        data.push(`${Utils.formattedNumber(value)} ${option.unitOfMeasure}`);
        const dpCurrentVal = dp.yValues?.find(y => y.label === option.label);
        translatedLabel = getLocalizedLabel(dpCurrentVal?.localizedContents, i18n.language, 'label') ?? option.label;
      }
      datasets.push({
        label: translatedLabel,
        data: data,
      });
    });
    setChartData({
      labels: [],
      datasets: datasets
    });
  };

  const isMonthyInterval = (g) => {
    return g?.timeInterval === 'MONTHLY';
  };

  const isYearlyInterval = (g) => {
    return g?.timeInterval === 'YEARLY';
  };

  const getLegendAlign = (g) => {
    const splitLegendPos = g.properties.options.legendPosition?.split(' ');
    const legendAlign = splitLegendPos[1] === 'LEFT'
      ? 'start' : splitLegendPos[1] === 'RIGHT'
        ? 'end' : 'center';
    return legendAlign;
  };

  const getLegendPosition = (g) => {
    const splitLegendPos = g.properties.options.legendPosition?.split(' ');
    const legendAlign = splitLegendPos[0] === 'TOP' ? 'top' : 'bottom';
    return legendAlign;
  };

  const getLegendPositionForPieChart = (g) => {
    const splitLegendPos = g.properties.options.legendPosition?.split(' ');
    const legendPosition = splitLegendPos[1] === 'LEFT'
      ? 'left' : splitLegendPos[1] === 'RIGHT'
        ? 'right' : 'bottom';
    return legendPosition;
  };

  const graphInfo = (
    <Popover id="popover-trigger-hover-focus" className="p-2">
      <div className="p-1">
      {(i18n.language && graph?.properties && graph?.properties[`${i18n.language}_description`]) ? graph?.properties[`${i18n.language}_description`] : graph?.properties?.description}
        </div>
    </Popover>
  );

  const renderGraphIconHolder = () => {
    if (isAutoGenerateGraphImg) return <></>;

    return (
      <div className='companyDashboard-iconBox'>
        <ul>
          <li>
            {graph?.type != 'PIE' && graphType === 'ESG' && renderYearMonthSwitch()}
          </li>
          <li>
            <OverlayTrigger
              placement="top"
              overlay={Utils.renderTooltip(
                t("DataLabelText.label")
              )}>
              <Form>
                <Form.Check // prettier-ignore
                  type="switch"
                  id="custom-switch"
                  checked={showDatalabel}
                  onChange={() => { setShowDatalabel(!showDatalabel) }}
                />
              </Form>
            </OverlayTrigger>
          </li>
          <li key={`key-iconBox-2`}>
            <OverlayTrigger
              trigger={['hover', 'focus']}
              placement="bottom"
              overlay={graphInfo}>
              <span><BsInfoCircle /></span>
            </OverlayTrigger>
          </li>
          <li>
            <OverlayTrigger
              placement="top"
              overlay={Utils.renderTooltip(
                t("More.label")
              )}>
              {renderMoreOptions({
                graphType: graph?.type,
                graphTitle: (i18n.language && graph?.properties?.[i18n.language])
                  ? graph.properties[i18n.language]
                  : graph?.properties?.title,
                id: config?.id,
                graphConfigType: graphType
              })}
            </OverlayTrigger>
          </li>
          {
            graphType === 'ESG' &&
            <li key={`key-iconBox-3`} className="cursor drag-handle">
              <AiOutlineDrag />
            </li>
          }
        </ul>
      </div>
    );
  };

  const renderFooter = () => {
    const elements = [];
    const leftElements = [];
    const rightElements = [];
    graph?.footers?.forEach((f, index) => {
      const uom = graph?.dataPointOptions?.find(dp => dp.label === f.label)?.unitOfMeasure ?? '';
      const dpConfig = config?.dataPoints?.find(dp => dp.label === f.label && f.position === dp.position);
      const hasIncreased = f.currentValue > f.comparedValue;
      const element = (
        <OverlayTrigger
          placement="top"
          overlay={Utils.renderTooltip(
            `${t("TotalText.label")} ${year}`
          )}>
          <div key={`footerSubEl-${index}`} id={`footerSubEl-${index}`} className="Energy_Produced_card-footer_in">
            <label className={isAutoGenerateGraphImg ? 'fs-5' : ''}>
            {t("Total.label")}
            </label>
            {
              isAutoGenerateGraphImg ? <h4>{`${Utils.formattedNumber(f.value)} ${uom}`}</h4> : <h6>{`${Utils.formattedNumber(f.value)} ${uom}`}</h6>
            }
          </div>
        </OverlayTrigger>
      );
      const rightElement = (
        <OverlayTrigger
          placement="top"
          overlay={Utils.renderTooltip(
            `${t("TrendText.label")} ${year - 1}`
          )}>
          {
            isAutoGenerateGraphImg
              ?
              <div key={`footerSubEl-${index}`} id={`footerSubEl-${index}`} className="Energy_Produced_card-footer_in">
                {
                  dpConfig?.comparison && hasIncreased
                    ?
                    <label className={dpConfig?.comparisonStyle === 'HIGHER THE BETTER' ? 'text-success' : 'text-danger'}>
                      <BiTrendingUp className="font-20" />
                    </label>
                    :
                    <label className={dpConfig?.comparisonStyle === 'HIGHER THE BETTER' ? 'text-danger' : 'text-success'}>
                      <BiTrendingDown className="font-20" />
                    </label>
                }
                <h4>{`${f.value} ${uom}`}</h4>
              </div>
              :
              <div key={`footerSubEl-${index}`} id={`footerSubEl-${index}`} className="Energy_Produced_card-footer_in">
                {
                  dpConfig?.comparison && hasIncreased
                    ?
                    <label className={dpConfig?.comparisonStyle === 'HIGHER THE BETTER' ? 'text-success' : 'text-danger'}>
                      <BiTrendingUp className="font-18" />
                    </label>
                    :
                    <label className={dpConfig?.comparisonStyle === 'HIGHER THE BETTER' ? 'text-danger' : 'text-success'}>
                      <BiTrendingDown className="font-18" />
                    </label>
                }
                <h6>{`${f.value} ${uom}`}</h6>
              </div>
          }
        </OverlayTrigger>
      );
      if (f.position === 'BOTTOM LEFT') {
        leftElements.push(element);
      } else if (f.position === 'BOTTOM RIGHT') {
        rightElements.push(rightElement);
      }
    });

    let footerCount = leftElements.length + rightElements.length;
    if (footerCount % 2 !== 0) {
      footerCount += 1;
    }
    footerCount /= 2;
    for (let index = 0; index < footerCount; index++) {
      const left = leftElements.splice(0, 1);
      const right = rightElements.splice(0, 1);
      const fclassName = graph?.type === 'KPI' ? '' : 'Energy_Produced_card-footer';
      elements.push(
        <div key={`footerEl-${index}`} id={`footerEl-${index}`}
          className={fclassName}>
          {left}{right}
        </div>
      );
    }
    return elements;
  };

  const renderPieChartLegends = () => {
    return (
      <div className="Energy_Produceddata_graph">
        <ul>
          {
            graph?.dataPointOptions?.filter(o => !o.position)?.map((option, index) => {
              const color = Utils.getDecodedHexForGraphColor(option.color);
              return (
                <li key={`pieLegend-${index}`} id={`pieLegend-${index}`}>
                  <span style={{
                    border: `3px solid ${color.borderColor}`,
                    backgroundColor: color.backgroundColor
                  }}></span>{option.label}</li>
              );
            })
          }
        </ul>
      </div>
    )
  };

  const getCardClassName = () => {
    switch (graph?.type) {
      case 'KPI':
        return 'Energy_Intensity_card border-top-0';
      default:
        return 'Energy_Produced_card';
    }
  };

  const renderKPICard = () => {
    const uom = graph?.dataPointOptions?.find(dp =>
      dp.label === chartData?.datasets[0]?.label)?.unitOfMeasure ?? '';
    const data = chartData?.datasets[0]?.data;
    const hasUnit = uom !== undefined && uom !== null;
    const displayValue = hasUnit ? data : `${data} ${uom}`;
    let className = '';
    if (isAutoGenerateGraphImg) {
      className = 'ESG_ShortValue_02';
    } else {
      className = 'ESG_ShortValue_01';
    }
    if (graphType !== 'ESG') {
      className += ' card p-2 flex-fill';
    }
    return (
      <div className={className}>
        {!isAutoGenerateGraphImg && graphType === 'ESG' &&
          <div className="ESG_ShortValue_01_btn cursor">
            <span className="mx-2" onClick={() => onDeactivate(config.id)}><CgClose /></span>
            <span className="drag-handle"><AiOutlineDrag /></span>
          </div>
        }
        {
          <>
          {isAutoGenerateGraphImg ? <h2>{displayValue}</h2> : <h4>{displayValue}</h4>}
          {graphType === 'ESG' && <div className="Energy_Produced_card-footer">{renderFooter()}</div> }
          <p>{chartData?.datasets[0]?.label}</p>
          </>
        }
      </div>
    );
  };

  const renderYearMonthSwitch = () => {
    return (
      <div className="month_year_selectbox">
        <Form.Check
          inline
          label="M"
          name={`monthRadio-${graph?.id}`}
          type="radio"
          id={`monthRadio-${graph?.id}`}
          value={'MONTHLY'}
          checked={activeInterval === 'MONTHLY' ? 'checked' : ''}
          onChange={() => {
            setActiveInterval('MONTHLY');
            getGraph(graph.id, year, standardId, 'MONTHLY')
          }}
        />
        <Form.Check
          inline
          label="Y"
          name={`yearRadio-${graph?.id}`}
          type="radio"
          id={`yearRadio-${graph?.id}`}
          value={'YEARLY'}
          checked={activeInterval === 'YEARLY' ? 'checked' : ''}
          onChange={() => {
            setActiveInterval('YEARLY');
            getGraph(graph.id, year, standardId, 'YEARLY', graph.properties.options.timeInterval == 'MONTHLY' ? 3 : graph.properties.options.yearOffset ?? 3);
          }}
        />
      </div>
    );
  };

  function findMaxYValue(datapoints) {
    let largestValue = 0;

    for (const datapoint of datapoints) {
      for (const yValue of datapoint.yValues) {
        if (yValue.value > largestValue) {
          largestValue = yValue.value;
        }
      }
    }
    return largestValue;
  }

  const setupGraphForGHGEmission = () => {
    let graphData = {}, chartDataClone = {};
    if (config.type === 'KPI') {
      graphData = {
        type: 'KPI'
      };
      chartDataClone = {
        datasets: [{ label: config.label, data: config.data }]
      };
      setGraph(graphData);
      setChartData(chartDataClone);
    } else {
      setupGraphFrom(config);
    }
  };

  const setupGraphFrom = (data, id, interval, yearOffset) => {
    const obj = data;
    obj['id'] = id;
    if (interval) {
      obj['timeInterval'] = interval;
    } else if (yearOffset > 0 || obj.properties?.options?.timeInterval === 'YEARLY') {
      obj['timeInterval'] = 'YEARLY';
    } else {
      obj['timeInterval'] = 'MONTHLY';
    }
    if (interval == '') {
      setActiveInterval(obj.properties.options.timeInterval);
    } 
    const transformedData = [obj.properties].map((objDat) => transformObject(objDat)); 
    if(transformedData[0]) {
      obj['properties'] = transformedData[0];
    }   
    setGraph(obj);
    setupDataForTheGraph(data);
  };

  const getGradientBackgroundColor = (color)  => {
    let gradientBackgroundColor = color.backgroundColor;
    if (chartRef && chartRef.current) {
        const chart = chartRef.current;
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        gradient.addColorStop(0, `${color.backgroundColor}00`); // Start fully transparent at the bottom
        gradient.addColorStop(0.5, `${color.backgroundColor}80`); // 50% opaque in the middle
        gradient.addColorStop(1, `${color.backgroundColor}FF`); // Fully opaque at the top
        gradientBackgroundColor = gradient;
    }
    return gradientBackgroundColor
  };

  return (
    <>
      {
        graph?.type === 'KPI'
          ? renderKPICard()
          :
          <div className={`card ${getCardClassName()}`}>
            <div className="Energy_Produced_card-header">
              {
                isAutoGenerateGraphImg
                  ?
                  <h4 className="m-0">
                    {(i18n.language && graph?.properties[i18n.language]) ? graph?.properties[i18n.language] : graph?.properties?.title}
                  </h4>
                  :
                  <h6 className="m-0">
                    {(i18n.language && graph?.properties[i18n.language]) ? graph?.properties[i18n.language] : graph?.properties?.title}
                  </h6>
              }
              {renderGraphIconHolder()}
            </div>
            <div className={`card-body pt-0 ${graph?.type === 'PIE' ? 'row m-0 align-items-center' : ''}`}>
              {renderFooter()}
              <div>{getChart(graph?.type, chartRef)}</div>
            </div>
          </div>
      }
      {renderDisplayModal('COMPANY_GRAPH', graph, year, config)}
    </>
  );
};
export default withTranslation()(CompanyGraphCard);
